import { CommonAction } from '../../../login/redux/types';
import { DataRoom } from '../../deals/redux/types';
import InvestmentTypes, {
    Aggregate,
    Cashflow,
    Distribution,
    ExecutedAgreements,
    FinancialCovenant,
    Guarantees,
    InvestmentDetails,
    Investments,
    Performances,
    PPM,
    RepaymentInfo,
    ReportingObligation,
    Security,
    UpdatePortfolioDoc,
} from './types';

function getInvestmentsReq(): CommonAction {
    return {
        type: InvestmentTypes.GET_INVESTMENTS_REQ,
        payload: undefined,
    };
}

function setInvestments(investments: Investments[]): CommonAction {
    return {
        type: InvestmentTypes.SET_INVESTMENTS,
        payload: investments,
    };
}

function fetchRepaymentReq(investmentUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_REPAYMENT_REQ,
        payload: investmentUUID,
    };
}

function setRepayment(repayment: RepaymentInfo[]): CommonAction {
    return {
        type: InvestmentTypes.SET_REPAYMENT,
        payload: repayment,
    };
}

function fetchInvestmentDetails(investmentUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_INVESTMENT_DETAILS,
        payload: investmentUUID,
    };
}

function setInvestmentDetails(investmentDetails?: InvestmentDetails): CommonAction {
    return {
        type: InvestmentTypes.SET_INVESTMENT_DETAILS,
        payload: investmentDetails,
    };
}

function fetchExecutedAgreements(dealUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_EXECUTED_AGREEMENTS,
        payload: dealUUID,
    };
}

function setExecutedAgreements(executedAgreements?: DataRoom[]): CommonAction {
    return {
        type: InvestmentTypes.SET_EXECUTED_AGREEMENTS,
        payload: executedAgreements,
    };
}

function fetchUpdatesOrReporties(dealUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_UPDATED_OR_REPORTIES,
        payload: dealUUID,
    };
}

function setUpdatesOrReporties(updatesOrReporties?: DataRoom[]): CommonAction {
    return {
        type: InvestmentTypes.SET_UPDATED_OR_REPORTIES,
        payload: updatesOrReporties,
    };
}

function fetchPerformances(uuid: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_PERFORMANCES,
        payload: uuid,
    };
}

function setPerformances(performances?: Performances): CommonAction {
    return {
        type: InvestmentTypes.SET_PERFORMANCES,
        payload: performances,
    };
}

function fetchDistributions(uuid: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_DISTRIBUTION,
        payload: uuid,
    };
}

function setDistributions(distributions?: { lastUpdatedOn: string; distributions?: Distribution[] }): CommonAction {
    return {
        type: InvestmentTypes.SET_DISTRIBUTION,
        payload: distributions,
    };
}

function fetchPPM(dealUUID: string, investorUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_PPM,
        payload: {
            dealUUID,
            investorUUID,
        },
    };
}

function setPPM(ppm?: PPM): CommonAction {
    return {
        type: InvestmentTypes.SET_PPM,
        payload: ppm,
    };
}

function fetchReportingObligation(issuerUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_REPORTING_OBLIGATION,
        payload: issuerUUID,
    };
}

function setReportingObligations(reportingObligations: ReportingObligation[]): CommonAction {
    return {
        type: InvestmentTypes.SET_REPORTING_OBLIGATION,
        payload: reportingObligations,
    };
}

function fetchCashflow(uuid: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_CASHFLOW,
        payload: uuid,
    };
}

function setCashflow(cashflow: Cashflow[]): CommonAction {
    return {
        type: InvestmentTypes.SET_CASHFLOW,
        payload: cashflow,
    };
}

function fetchGuarantees(investmentUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_GUARANTEES,
        payload: investmentUUID,
    };
}

function setGuarantees(list: Guarantees[]): CommonAction {
    return {
        type: InvestmentTypes.SET_GUARANTEES,
        payload: list,
    };
}

function fetchSecurities(investerUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_SECURITIES,
        payload: investerUUID,
    };
}

function setSecurities(list: Security[]): CommonAction {
    return {
        type: InvestmentTypes.SET_SECURITIES,
        payload: list,
    };
}

function fetchCovenants(investmentUUID: string): CommonAction {
    return {
        type: InvestmentTypes.FETCH_COVENANTS,
        payload: investmentUUID,
    };
}

function setCovenants(list: FinancialCovenant[]): CommonAction {
    return {
        type: InvestmentTypes.SET_COVENANTS,
        payload: list,
    };
}

function fetchAggregate(): CommonAction {
    return {
        type: InvestmentTypes.FETCH_AGGREGATE,
        payload: undefined,
    };
}

function setAggregate(aggregate?: Aggregate): CommonAction {
    return {
        type: InvestmentTypes.SET_AGGREGATE,
        payload: aggregate,
    };
}

function fetchUpdatedDocuments(): CommonAction {
    return {
        type: InvestmentTypes.FETCH_UPDATE_DOCUMENTS,
        payload: undefined,
    };
}

function setUpdatedDocuments(list: UpdatePortfolioDoc[]): CommonAction {
    return {
        type: InvestmentTypes.SET_UPDATE_DOCUMENTS,
        payload: list,
    };
}

function fetchInvestmentExecutedAgreements(): CommonAction {
    return {
        type: InvestmentTypes.FETCH_INVESTMENT_EXECUTED_AGREEMENTS,
        payload: undefined,
    };
}

function setInvestmentExecutedAgreements(list: ExecutedAgreements[]): CommonAction {
    return {
        type: InvestmentTypes.SET_INVESTMENT_EXECUTED_AGREEMENTS,
        payload: list,
    };
}

const InvestmentsActions = {
    getInvestmentsReq,
    setInvestments,
    fetchRepaymentReq,
    setRepayment,
    fetchInvestmentDetails,
    setInvestmentDetails,
    fetchExecutedAgreements,
    setExecutedAgreements,
    fetchUpdatesOrReporties,
    setUpdatesOrReporties,
    fetchPerformances,
    setPerformances,
    fetchDistributions,
    setDistributions,
    fetchPPM,
    setPPM,
    fetchReportingObligation,
    setReportingObligations,
    fetchCashflow,
    setCashflow,
    fetchGuarantees,
    setGuarantees,
    fetchSecurities,
    setSecurities,
    fetchCovenants,
    setCovenants,
    fetchAggregate,
    setAggregate,
    fetchUpdatedDocuments,
    setUpdatedDocuments,
    fetchInvestmentExecutedAgreements,
    setInvestmentExecutedAgreements,
};

export default InvestmentsActions;
